import { EFinancingProgram } from '@if/core/types'
import {
  AutoWorksheet,
  AutoWorksheetDecisionDTO,
  AutoWorksheetRevision,
  FullCreditApplication,
  VehicleEvaluationDTO,
} from '@src/data/types'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'

export function transformWorksheetRevisionFromApi<T extends AutoWorksheet>(revision: T): T {
  const ret = revision

  ret.vehicleAccAndFees = computeFeesAndAccessories(revision)

  return ret
}

const computeFeesAndAccessories = (worksheet: AutoWorksheet) => {
  if (worksheet)
    return (
      (worksheet.dealerAdminFee ?? 0) +
      (worksheet.lenderFee ?? 0) +
      (worksheet.icebergPPSA ?? 0) +
      (worksheet.installationDelivery ?? 0) +
      (worksheet.licenseFee ?? 0) +
      (worksheet.otherNonTaxable ?? 0) +
      (worksheet.otherTaxable ?? 0)
    )

  return 0
}

const baseUrl = '/Applications/auto/{creditApplicationId}/worksheet'

function buildUrl(creditApplicationId: string): string {
  return baseUrl.replace('{creditApplicationId}', creditApplicationId)
}

// API Functions
export async function getAutoWorksheet(creditApplicationId: string): Promise<AutoWorksheet> {
  const apiClient = getApiClient()
  const response = await apiClient.get<AutoWorksheet>(buildUrl(creditApplicationId))
  return transformWorksheetRevisionFromApi(response.data)
}

export async function createAutoWorksheet(creditApplicationId: string): Promise<AutoWorksheet> {
  const apiClient = getApiClient()
  const url = buildUrl(creditApplicationId)
  const response = await apiClient.post<AutoWorksheet>(url, { creditApplicationId })
  return response.data
}

export async function updateAutoWorksheet(data: Partial<AutoWorksheet>): Promise<AutoWorksheet> {
  const apiClient = getApiClient()
  const url = buildUrl(data.creditApplicationId!)
  const response = await apiClient.put<AutoWorksheet>(url, data)
  return response.data
}

export async function updateVehicleEvaluation(data: VehicleEvaluationDTO): Promise<AutoWorksheet> {
  const apiClient = getApiClient()
  const url = `${buildUrl(data.creditApplicationId!)}/VehicleEvaluation`
  const response = await apiClient.put<AutoWorksheet>(url, data)
  return response.data
}

export async function updateWorksheetDecision(data: AutoWorksheetDecisionDTO): Promise<AutoWorksheet> {
  const apiClient = getApiClient()
  const url = `${buildUrl(data.creditApplicationId!)}/Decision`
  const response = await apiClient.put<AutoWorksheet>(url, data)
  return response.data
}

export async function getWorksheetRevisions(creditApplicationId: string): Promise<AutoWorksheetRevision[]> {
  const apiClient = getApiClient()
  const url = `${buildUrl(creditApplicationId)}/Revisions`
  const response = await apiClient.get<AutoWorksheetRevision[]>(url)
  return response.data.map(transformWorksheetRevisionFromApi)
}

export const autoWorksheetKeys = {
  all: ['autoWorksheet'] as const,
  detail: (creditApplicationId: string) => [...autoWorksheetKeys.all, creditApplicationId] as const,
  revisions: (creditApplicationId: string) => [...autoWorksheetKeys.all, 'revisions', creditApplicationId] as const,
}

export function useGetAutoWorksheet(creditApplicationId: string | undefined) {
  return useQuery({
    queryKey: autoWorksheetKeys.detail(creditApplicationId ?? ''),
    queryFn: () => getAutoWorksheet(creditApplicationId!),
    enabled: !!creditApplicationId,
  })
}

export function useCreateAutoWorksheet(): [MutationFunction<AutoWorksheet, string>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: createAutoWorksheet,
    onSuccess: (createdWorksheet) => {
      // Update the worksheet-specific query
      queryClient.setQueryData(autoWorksheetKeys.detail(createdWorksheet.creditApplicationId), createdWorksheet)
      // Update the full credit application query
      const queryKey = fullCreditApplicationKeys.detail(createdWorksheet.creditApplicationId, EFinancingProgram.Auto)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: createdWorksheet,
        })
      }
      void queryClient.invalidateQueries({
        queryKey: autoWorksheetKeys.revisions(createdWorksheet.creditApplicationId),
      })
    },
  })

  return [mutateAsync, isPending]
}

export function useUpdateAutoWorksheet(): [MutationFunction<AutoWorksheet, Partial<AutoWorksheet>>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateAutoWorksheet,
    onSuccess: (updatedWorksheet) => {
      // Update the worksheet-specific query
      queryClient.setQueryData(autoWorksheetKeys.detail(updatedWorksheet.creditApplicationId), updatedWorksheet)
      // Update the full credit application query
      const queryKey = fullCreditApplicationKeys.detail(updatedWorksheet.creditApplicationId, EFinancingProgram.Auto)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
      void queryClient.invalidateQueries({
        queryKey: autoWorksheetKeys.revisions(updatedWorksheet.creditApplicationId),
      })
    },
  })

  return [mutateAsync, isPending]
}

export function useUpdateVehicleEvaluation(): [MutationFunction<AutoWorksheet, VehicleEvaluationDTO>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateVehicleEvaluation,
    onSuccess: (updatedWorksheet) => {
      // Update the worksheet-specific query
      queryClient.setQueryData(autoWorksheetKeys.detail(updatedWorksheet.creditApplicationId), updatedWorksheet)
      // Update the full credit application query
      const queryKey = fullCreditApplicationKeys.detail(updatedWorksheet.creditApplicationId, EFinancingProgram.Auto)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
      void queryClient.invalidateQueries({
        queryKey: autoWorksheetKeys.revisions(updatedWorksheet.creditApplicationId),
      })
    },
  })

  return [mutateAsync, isPending]
}

export function useUpdateWorksheetDecision(): [MutationFunction<AutoWorksheet, AutoWorksheetDecisionDTO>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateWorksheetDecision,
    onSuccess: (updatedWorksheet) => {
      // Update the worksheet-specific query
      queryClient.setQueryData(autoWorksheetKeys.detail(updatedWorksheet.creditApplicationId), updatedWorksheet)
      // Update the full credit application query
      const queryKey = fullCreditApplicationKeys.detail(updatedWorksheet.creditApplicationId, EFinancingProgram.Auto)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
      void queryClient.invalidateQueries({
        queryKey: autoWorksheetKeys.revisions(updatedWorksheet.creditApplicationId),
      })
    },
  })

  return [mutateAsync, isPending]
}

export function useGetWorksheetRevisions(creditApplicationId: string | undefined): [AutoWorksheetRevision[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: autoWorksheetKeys.revisions(creditApplicationId ?? ''),
    queryFn: () => getWorksheetRevisions(creditApplicationId!),
    enabled: !!creditApplicationId,
  })
  return [data ?? [], isFetching]
}
